import {
  MASTERY_CATALOG,
  MASTERY_PAGE_PRESETS,
  MASTERY_TOTAL_POINTS,
  MASTERY_TREE_CAP
} from './masteryCatalog.js';

const EMPTY_STATS = Object.freeze({
  damageAmp: 0,
  executeDamageAmp: 0,
  armorPen: 0,
  armor: 0,
  mr: 0,
  goldPer10: 0,
  buffDurationAmp: 0
});

export function createMasteryPage({
  id,
  name,
  trees = { offense: 0, defense: 0, utility: 0 },
  ranks = {}
} = {}) {
  return {
    id: id || 'custom',
    name: name || 'Custom',
    trees: {
      offense: trees.offense || 0,
      defense: trees.defense || 0,
      utility: trees.utility || 0
    },
    ranks: { ...ranks }
  };
}

export function resolveMasteryPage(pageId) {
  const preset = MASTERY_PAGE_PRESETS[pageId];
  if (!preset) return null;
  return createMasteryPage(preset);
}

export function validateMasteryPage(page) {
  if (!page || !page.trees) return { ok: false, reason: 'MISSING' };
  const { offense, defense, utility } = page.trees;
  const total = offense + defense + utility;
  if (total !== MASTERY_TOTAL_POINTS) {
    return { ok: false, reason: 'TOTAL' };
  }
  if (offense > MASTERY_TREE_CAP || defense > MASTERY_TREE_CAP
    || utility > MASTERY_TREE_CAP) {
    return { ok: false, reason: 'TREE_CAP' };
  }

  const spentByTree = { offense: 0, defense: 0, utility: 0 };
  for (const [id, rank] of Object.entries(page.ranks || {})) {
    const def = MASTERY_CATALOG[id];
    if (!def) return { ok: false, reason: 'UNKNOWN' };
    if (rank < 0 || rank > def.maxRank) {
      return { ok: false, reason: 'RANK' };
    }
    spentByTree[def.tree] += rank;
  }
  for (const tree of ['offense', 'defense', 'utility']) {
    if (spentByTree[tree] > page.trees[tree]) {
      return { ok: false, reason: 'TREE_BUDGET' };
    }
  }
  return { ok: true };
}

export function aggregateMasteryStats(page) {
  const acc = { ...EMPTY_STATS };
  if (!page || !page.ranks) return acc;
  for (const [id, rank] of Object.entries(page.ranks)) {
    const def = MASTERY_CATALOG[id];
    if (!def || !rank) continue;
    for (const [stat, per] of Object.entries(def.perRank)) {
      acc[stat] = (acc[stat] || 0) + per * rank;
    }
  }
  return acc;
}

export function applyMasteryPageToEntity(entity, page) {
  if (!entity || !page) return { ok: false, reason: 'MISSING' };
  const validation = validateMasteryPage(page);
  if (!validation.ok) return validation;

  stripPreviousMasteryFlats(entity);
  const stats = aggregateMasteryStats(page);
  entity.masteryStats = { ...stats };
  entity.masteryPageId = page.id;
  const runeGp = (entity.runeStats && entity.runeStats.goldPer10) || 0;
  entity.goldPer10 = runeGp + (stats.goldPer10 || 0);

  entity.armor = (entity.armor || 0) + stats.armor;
  entity.mr = (entity.mr || 0) + stats.mr;
  entity.armorPen = (entity.armorPen || 0) + stats.armorPen;
  return { ok: true, stats };
}

function stripPreviousMasteryFlats(entity) {
  const prev = entity.masteryStats;
  if (!prev) return;
  entity.armor = (entity.armor || 0) - (prev.armor || 0);
  entity.mr = (entity.mr || 0) - (prev.mr || 0);
  entity.armorPen = (entity.armorPen || 0) - (prev.armorPen || 0);
}
